import { getNestedProperty } from '@opallabs/lambda'
import {
  Assignment,
  ContentCollection,
  ContextCollection,
  MomentCollection,
  NormalizedAssignment,
  NormalizedAssignmentContext,
  PhaseCollection,
  PhaseItem,
  ServiceCollection,
  StageCollection,
} from '../../types'

type PhaseItemsToNormalizedAssignmentsArgObject = {
  contents: ContentCollection
  phaseItems: PhaseItem[]
  phases: PhaseCollection
  services: ServiceCollection
}

type MapPhaseItemsToNormalizedAssignments = (
  arg: PhaseItemsToNormalizedAssignmentsArgObject
) => NormalizedAssignment[]

export const mapPhaseItemsToNormalizedAssignments: MapPhaseItemsToNormalizedAssignments =
  ({ contents, phaseItems, phases, services }) =>
    phaseItems.map((phaseItem) => {
      let assigneeId = getNestedProperty<string>(
        phaseItem,
        'relationships',
        'to_user',
        'id'
      )
      if (assigneeId === undefined) {
        assigneeId = ''
      }

      let dueDate = ''
      let name = ''
      const phaseId = getNestedProperty<string>(
        phaseItem,
        'relationships',
        'phase',
        'id'
      )
      if (phaseId !== undefined) {
        const phase = phases[phaseId]
        if (phase !== undefined) {
          dueDate = phase.due_date || ''
          name = phase.name
        }
      }

      let scheduledAt: string | null = ''
      const mergedAssignmentContext: NormalizedAssignmentContext = {
        id: '',
        name: '',
        type: 'content',
        momentId: '',
        accountId: '',
        status: '',
      }
      let service = null
      const contentId = getNestedProperty<string>(
        phaseItem,
        'relationships',
        'approvable',
        'id'
      )
      if (contentId !== undefined) {
        const content = contents[contentId]
        if (content !== undefined) {
          scheduledAt = content.scheduled_at
          mergedAssignmentContext.id = content.id
          mergedAssignmentContext.name = content.name
          const accountId = getNestedProperty<string>(
            content,
            'relationships',
            'account',
            'id'
          )
          if (accountId !== undefined) {
            mergedAssignmentContext.accountId = accountId
          }
          const momentId = getNestedProperty<string>(
            content,
            'relationships',
            'moment',
            'id'
          )
          if (momentId !== undefined) {
            mergedAssignmentContext.momentId = momentId
          }
          mergedAssignmentContext.status = content.status || ''
          const serviceId = getNestedProperty<string>(
            content,
            'relationships',
            'service',
            'id'
          )
          if (serviceId !== undefined) {
            service = services[serviceId]
          }
        }
      }

      return {
        assigneeId,
        context: mergedAssignmentContext,
        dueDate,
        id: `phase_item_${phaseItem.id}`,
        name,
        originalId: phaseItem.id,
        scheduledAt,
        service,
        type: phaseItem.request || 'Approval',
      }
    })

type AssignmentsToNormalizedAssignmentsArgObject = {
  assignments: Assignment[]
  assignmentsContextMap: ContextCollection
  moments: MomentCollection
  stages: StageCollection
}

type MapAssignmentsToNormalizedAssignments = (
  arg: AssignmentsToNormalizedAssignmentsArgObject
) => NormalizedAssignment[]

export const mapAssignmentsToNormalizedAssignments: MapAssignmentsToNormalizedAssignments =
  ({ assignments, assignmentsContextMap, moments, stages }) =>
    assignments.map((assignment) => {
      let assigneeId = getNestedProperty<string>(
        assignment,
        'relationships',
        'assigned_user',
        'id'
      )
      if (assigneeId === undefined) {
        assigneeId = ''
      }

      let dueDate = ''
      let scheduledAt: string | null = null
      let title = ''
      const mergedAssignmentContext: NormalizedAssignmentContext = {
        id: '',
        name: '',
        type: 'moment',
        momentId: null,
        accountId: '',
        status: '',
      }
      const service = null
      const stageId = getNestedProperty<string>(
        assignment,
        'relationships',
        'stage',
        'id'
      )
      if (stageId !== undefined) {
        const stage = stages[stageId]
        if (stage !== undefined) {
          const context = assignmentsContextMap[assignment.id]
          const momentId = getNestedProperty<string>(
            context,
            'relationships',
            'moment',
            'id'
          )
          if (momentId !== undefined) {
            const moment = moments[momentId]
            if (moment !== undefined) {
              scheduledAt = moment.scheduled_at || null
              const brandId = getNestedProperty<string>(
                moment,
                'relationships',
                'brand',
                'id'
              )
              if (brandId !== undefined) {
                mergedAssignmentContext.accountId = brandId
              }
              mergedAssignmentContext.name = moment.title || ''
              mergedAssignmentContext.id = moment.id
              mergedAssignmentContext.momentId = moment.id
              mergedAssignmentContext.status = stage.status || ''
            }
          }
          dueDate = stage.due_date || ''
          if (stage.metadata !== undefined && stage.metadata !== null) {
            title = stage.metadata.title || ''
          }
        }
      }

      const getDisplayType = (): string => {
        switch (assignment.assignment_type) {
          case 'circuit_breaker':
            return 'Pause'
          case 'approval':
            return 'Approval'
          case 'task':
            if (
              assignment.metadata !== undefined &&
              assignment.metadata !== null
            ) {
              return assignment.metadata.title || 'Task'
            }
            return 'Task'
          default:
            return 'Task'
        }
      }

      return {
        assigneeId,
        context: mergedAssignmentContext,
        dueDate,
        id: `assignment_${assignment.id}`,
        name: title,
        originalId: assignment.id,
        scheduledAt,
        service,
        type: getDisplayType(),
      }
    })
